import { Percentile } from "../../../../../graphql/types/graphql";
import { AnalysisDriverType } from "../../types/types";
import { DriverTableSegment } from "../driverTableUtils";
import { HeadroomTableSegment } from "../headroomTableUtils";
import { TableSegment } from "../segmentationTableUtils";
import { Cell } from "./writeWorkSheet";

/**
 * Formats the header text for a driver or headroom analysis.
 *
 * @param driverNumber - The number of the driver (e.g., 1, 2). Only used when not in headroom mode.
 * @param driverType - The type of the driver (e.g., "ACV", "Revenue") or null if none.
 * @param numeratorType - The numerator type when mode is "PERCENTAGE", otherwise ignored.
 * @param mode - The analysis mode, e.g., "PERCENTAGE" or "ABSOLUTE".
 * @param headroom - Pass "headroom" to format as a headroom header (e.g., "Headroom - ACV").
 *                   If provided, only `driverType` is used — driver number and mode are ignored.
 *
 * @returns
 *   - In headroom mode: `"Headroom - ACV"` (or `"Headroom - None"` if no type)
 *   - Otherwise: Full driver header like `"Driver 1 - Sales as % of Revenue"` or `"Driver 2 - None"`
 */
export const formatDriverHeader = (
  driverNumber: number,
  driverType: string | null,
  numeratorType: string | null,
  mode: AnalysisDriverType,
  headroom?: "headroom" | null
): string => {
  if (headroom === "headroom") {
    const typeLabel = driverType || "None";
    return `Headroom Driver - ${typeLabel}`;
  }

  if (mode === "PERCENTAGE" && numeratorType && driverType) {
    return `Driver ${driverNumber} - ${numeratorType} as % of ${driverType}`;
  }

  return `Driver ${driverNumber}${driverType ? ` - ${driverType}` : " - None"}`;
};

/**
 * Creates header rows for the Excel worksheet.
 * @param segmentsToExport - Array of table segments to export.
 * @param header1 - First header label (e.g., "Total Customer").
 * @param header2 - Second header label (e.g., "Total Prospect").
 * @param driver1Type - Type of the first driver or null.
 * @param driver2Type - Type of the second driver or null.
 * @param driver1Mode - Analysis mode for the first driver.
 * @param driver2Mode - Analysis mode for the second driver.
 * @param driver1NumeratorType - Numerator type for the first driver in percentage mode or null.
 * @param driver2NumeratorType - Numerator type for the second driver in percentage mode or null.
 * @param segmentColumns - Number of segment columns.
 * @param totalColumn - Index of the total column.
 * @param header1Column - Index of the first header column.
 * @param header2Column - Index of the second header column.
 * @param driver1StartColumn - Starting column index for driver 1 metrics.
 * @param driver2StartColumn - Starting column index for driver 2 metrics.
 * @returns Array of header rows.
 */
export const createHeaders = (
  segmentsToExport: TableSegment[],
  header1: string,
  header2: string,
  driver1Type: string | null,
  driver2Type: string | null,
  driver1Mode: AnalysisDriverType,
  driver2Mode: AnalysisDriverType,
  driver1NumeratorType: string | null,
  driver2NumeratorType: string | null,
  segmentColumns: number,
  totalColumn: number,
  header1Column: number,
  header2Column: number,
  driver1StartColumn: number,
  driver2StartColumn: number
): Cell[][] => {
  const mainHeaderRow: Cell[] = Array(segmentColumns + 13).fill({ v: "" });
  mainHeaderRow[0] = { v: "Segments", s: { font: { bold: true }, alignment: { horizontal: "center" } } };
  mainHeaderRow[totalColumn] = { v: "Total #", s: { font: { bold: true }, alignment: { horizontal: "center" } } };
  mainHeaderRow[header1Column] = { v: "# Customers", s: { font: { bold: true }, alignment: { horizontal: "center" } } };
  mainHeaderRow[header2Column] = { v: "# Prospects", s: { font: { bold: true }, alignment: { horizontal: "center" } } };
  mainHeaderRow[driver1StartColumn] = {
    v: formatDriverHeader(1, driver1Type, driver1NumeratorType, driver1Mode),
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  };
  mainHeaderRow[driver2StartColumn] = {
    v: formatDriverHeader(2, driver2Type, driver2NumeratorType, driver2Mode),
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  };

  const subHeaderRow: Cell[] = [];
  const hasOneDataAndOneManualSegment =
    segmentsToExport.length === 2 &&
    segmentsToExport.some((seg) => seg.method === "DATA") &&
    segmentsToExport.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segmentsToExport.length === 1 && segmentsToExport.every((seg) => seg.method === "MANUAL");
  const isTwoSameMethod =
    segmentsToExport.length === 2 && segmentsToExport.every((seg) => seg.method === segmentsToExport[0].method);

  if (isManualOnly) {
    const manualSegment = segmentsToExport[0];
    subHeaderRow.push({
      v: "Type (Manual)",
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
    subHeaderRow.push({
      v: `Segment 1 - ${manualSegment?.segmentType || "Manual"} (Manual)`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
  } else if (hasOneDataAndOneManualSegment) {
    const manualSegment = segmentsToExport.find((seg) => seg.method === "MANUAL");
    const dataSegment = segmentsToExport.find((seg) => seg.method === "DATA");
    subHeaderRow.push({
      v: `${manualSegment?.segmentType || "Manual"} (Manual)`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
    subHeaderRow.push({
      v: `${dataSegment?.segmentType || "Data"} (Data)`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
  } else if (isTwoSameMethod) {
    segmentsToExport.forEach((seg: TableSegment) => {
      subHeaderRow.push({
        v: `Segment ${seg.index + 1} - ${seg.segmentType} (${seg.method === "DATA" ? "Data" : "Manual"})`,
        s: { font: { bold: true }, alignment: { horizontal: "center" } },
      });
    });
  } else {
    segmentsToExport.forEach((seg: TableSegment) => {
      subHeaderRow.push({
        v: `Segment ${seg.index + 1} - ${seg.segmentType} (${seg.method === "DATA" ? "Data" : "Manual"})`,
        s: { font: { bold: true }, alignment: { horizontal: "center" } },
      });
      subHeaderRow.push({ v: "", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
    });
  }

  subHeaderRow.push({ v: "Total", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({
    // v: header1.replace(/^Total /, ""),
    v: "# Customers",
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  });
  subHeaderRow.push({
    // v: header2.replace(/^Total /, ""),
    v: "# Prospects",
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  });
  ["Min", "25%", "50%", "75%", "Max"].forEach((metric) => {
    subHeaderRow.push({ v: metric, s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  });
  ["Min", "25%", "50%", "75%", "Max"].forEach((metric) => {
    subHeaderRow.push({ v: metric, s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  });

  return [mainHeaderRow, subHeaderRow];
};

/**
 * Formats a percentile value (enum or number) into a human-readable string label.
 * @param percentile - The percentile value to format, either a Percentile enum or a number.
 * @returns A string representing the formatted percentile label (e.g., "Percentile (Min)", "Percentile (50%)").
 */
const formatPercentileLabel = (percentile: Percentile | number): string => {
  if (typeof percentile === "number") {
    return `Percentile (${percentile}%)`;
  }

  switch (percentile) {
    case Percentile.Min:
      return "Percentile (Min)";
    case Percentile.P25:
      return "Percentile (25%)";
    case Percentile.P50:
      return "Percentile (50%)";
    case Percentile.P75:
      return "Percentile (75%)";
    case Percentile.Max:
      return "Percentile (Max)";
    default:
      return "Percentile";
  }
};

/**
 * Creates header rows for the Excel worksheet for a single driver.
 * @param segmentsToExport - Array of driver table segments to export.
 * @param driverNumber - The number of the driver (1 or 2).
 * @param driverType - Type of the selected driver or null.
 * @param driverMode - Analysis mode for the selected driver.
 * @param driverNumeratorType - Numerator type for the selected driver in percentage mode or null.
 * @param driverPercentile - Percentile for the selected driver.
 * @param segmentColumns - Number of segment columns.
 * @param driverStartColumn - Starting column index for driver metrics.
 * @returns Array of header rows.
 */
export const createDriverHeaders = (
  segmentsToExport: DriverTableSegment[],
  driverNumber: number,
  driverType: string | null,
  driverMode: AnalysisDriverType,
  driverNumeratorType: string | null,
  driverPercentile: number,
  segmentColumns: number,
  driverStartColumn: number
): Cell[][] => {
  const mainHeaderRow: Cell[] = Array(segmentColumns + 3).fill({ v: "" });
  mainHeaderRow[0] = { v: "Segments", s: { font: { bold: true }, alignment: { horizontal: "center" } } };
  mainHeaderRow[driverStartColumn] = {
    v: formatDriverHeader(driverNumber, driverType, driverNumeratorType, driverMode),
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  };

  const subHeaderRow: Cell[] = [];
  const hasOneDataAndOneManualSegment =
    segmentsToExport.length === 2 &&
    segmentsToExport.some((seg) => seg.method === "DATA") &&
    segmentsToExport.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segmentsToExport.length === 1 && segmentsToExport.every((seg) => seg.method === "MANUAL");
  const isTwoSameMethod =
    segmentsToExport.length === 2 && segmentsToExport.every((seg) => seg.method === segmentsToExport[0].method);

  if (isManualOnly) {
    const manualSegment = segmentsToExport[0];
    subHeaderRow.push({
      v: "Type (Manual)",
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
    subHeaderRow.push({
      v: `Segment 1 - ${manualSegment?.segmentType || "Manual"} - Manual`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
  } else if (hasOneDataAndOneManualSegment) {
    const manualSegment = segmentsToExport.find((seg) => seg.method === "MANUAL");
    const dataSegment = segmentsToExport.find((seg) => seg.method === "DATA");
    subHeaderRow.push({
      v: `${manualSegment?.segmentType || "Manual"} - Manual`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
    subHeaderRow.push({
      v: `${dataSegment?.segmentType || "Data"} - Data`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
  } else if (isTwoSameMethod) {
    segmentsToExport.forEach((seg: DriverTableSegment) => {
      subHeaderRow.push({
        v: `Segment ${seg.index + 1} - ${seg.segmentType} - ${seg.method}`,
        s: { font: { bold: true }, alignment: { horizontal: "center" } },
      });
    });
  } else {
    segmentsToExport.forEach((seg: DriverTableSegment) => {
      subHeaderRow.push({
        v: `Segment ${seg.index + 1} - ${seg.segmentType} - ${seg.method}`,
        s: { font: { bold: true }, alignment: { horizontal: "center" } },
      });
      subHeaderRow.push({ v: "", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
    });
  }

  subHeaderRow.push({ v: "Median", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Mean", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({
    v: formatPercentileLabel(driverPercentile),
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  });

  return [mainHeaderRow, subHeaderRow];
};

/**
 * Creates header rows for the Excel worksheet for HeadroomFinalTable.
 */
export const createHeadroomFinalHeaders = (
  segments: HeadroomTableSegment[],
  driverType: string | null,
  driverMode: AnalysisDriverType,
  driverNumeratorType: string | null,
  segmentColumns: number,
  driverStartColumn: number
): Cell[][] => {
  const mainHeaderRow: Cell[] = Array(segmentColumns + 11).fill({ v: "" });
  mainHeaderRow[0] = { v: "Segments", s: { font: { bold: true }, alignment: { horizontal: "center" } } };
  const driver = driverMode === "PERCENTAGE" ? `${driverNumeratorType} as % of ${driverType}` : `${driverType}`;

  mainHeaderRow[driverStartColumn + 3] = {
    v: formatDriverHeader(1, driverType, driverNumeratorType, driverMode, "headroom"),
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  };

  const subHeaderRow: Cell[] = [];
  const hasOneDataAndOneManualSegment =
    segments.length === 2 &&
    segments.some((seg) => seg.method === "DATA") &&
    segments.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (isManualOnly) {
    const manualSegment = segments[0];
    subHeaderRow.push({
      v: "Type (Manual)",
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
    subHeaderRow.push({
      v: `Segment 1 - ${manualSegment?.segmentType || "Manual"} (Manual)`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
  } else if (hasOneDataAndOneManualSegment) {
    const manualSegment = segments.find((seg) => seg.method === "MANUAL");
    const dataSegment = segments.find((seg) => seg.method === "DATA");
    subHeaderRow.push({
      v: `${manualSegment?.segmentType || "Manual"} (Manual)`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
    subHeaderRow.push({
      v: `${dataSegment?.segmentType || "Data"} (Data)`,
      s: { font: { bold: true }, alignment: { horizontal: "center" } },
    });
  } else if (isTwoSameMethod) {
    segments.forEach((seg: HeadroomTableSegment) => {
      subHeaderRow.push({
        v: `${seg.segmentType} (${seg.method === "DATA" ? "Data" : "Manual"})`,
        s: { font: { bold: true }, alignment: { horizontal: "center" } },
      });
    });
  } else {
    segments.forEach((seg: HeadroomTableSegment) => {
      subHeaderRow.push({
        v: `Segment ${seg.index + 1} - ${seg.segmentType} (${seg.method === "DATA" ? "Data" : "Manual"})`,
        s: { font: { bold: true }, alignment: { horizontal: "center" } },
      });
      subHeaderRow.push({ v: "", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
    });
  }

  subHeaderRow.push({ v: "# Customers", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "# Prospects", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Penetration (%)", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Driver Initial", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Driver Final", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Headroom", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Headroom (%)", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({
    v: "Increased Penetration (%)",
    s: { font: { bold: true }, alignment: { horizontal: "center" } },
  });
  subHeaderRow.push({ v: "Final # Customers", s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: `Total Initial ${driver}`, s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: `Total Final ${driver}`, s: { font: { bold: true }, alignment: { horizontal: "center" } } });
  subHeaderRow.push({ v: "Total Headroom", s: { font: { bold: true }, alignment: { horizontal: "center" } } });

  return [mainHeaderRow, subHeaderRow];
};
